import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { ICityDTO } from 'data/dtos/common/i-city-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import CitySchema, { ICityFormData } from 'schema/common/city'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const CityForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [countriesA, setCountriesA] = useState([])
  const [readonly, setReadonly] = useState(false)
  const [statesB, setStatesB] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<ICityFormData>({
    resolver: ajvResolver(CitySchema),
    defaultValues: {
      countryId: '',
      stateId: '',
      name: '',
      code: '',
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/cities/${id}`)
        .then((response) => {
          const { data } = response.data

          const cityResult = {
            countryId: data.countryId.id,
            stateId: data.stateId.id,
            name: data.name,
            code: data.code,
          }

          return cityResult
        })
        .then((cityResult: ICityDTO) => {
          reset(cityResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: ICityDTO) => {
    setIsLoading(true)

    const payLoad: ICityDTO = {
      countryId: data.countryId,
      stateId: data.stateId,
      name: data.name,
      code: data.code,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/cities`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/cities')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/cities', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  useEffect(() => {
    // setFocus(firstInputElement)
  }, [setFocus])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Cidades'
        subTitle='Aqui são informados dados sobre cidades.'
      />
      <Form.Body>
        <Form.Select
          label='País'
          error={!!errors.countryId}
          errorMessage={errors?.countryId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='countryId'
          rules={{
            onChange: (e) => {
              setValue('countryId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().countryId}`}
          options={countriesA}
        />

        <Form.Select
          label='Estado'
          error={!!errors.stateId}
          errorMessage={errors?.stateId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='stateId'
          rules={{
            onChange: (e) => {
              setValue('stateId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().stateId}`}
          options={statesB}
        />

        <Form.Input
          label='Nome'
          error={!!errors.name}
          errorMessage={errors?.name?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 10, lg: 10, xl: 10 }}
          maxLength={100}
          name='name'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Código'
          error={!!errors.code}
          errorMessage={errors?.code?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 2, lg: 2, xl: 2 }}
          maxLength={2}
          name='code'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default CityForm
